import type { EventTableKind } from "@hobbyshopify/backend-client";
import { BaseController, type Rx, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import {
  createTableKind,
  deleteTableKind,
  loadTableKinds,
  refundTable,
  updateTableKind,
} from "../../../../core/ops/hosted-event-ops";
import { errorText } from "../../../../core/primitives/errors";
import { hasErrors } from "../../../../core/primitives/form-errors";
import { REFUND_FAILED_ON_THE_WAY } from "../../../../core/primitives/hosted-event";
import {
  busyForm,
  failedForm,
  failedLoad,
  idleForm,
  idleLoad,
  loaded,
  loading,
} from "../../../../core/primitives/load-status";
import {
  draftToNewTableKind,
  draftToTableKindUpdate,
  EMPTY_TABLE_KIND,
  mayTakeBack,
  payingCount,
  TABLES_FAILED_ON_THE_WAY,
  TABLES_FAILED_TO_LOAD,
  type TableKindField,
  tableKindDraft,
  takenCount,
  validateTableKindDraft,
} from "../../../../core/primitives/table-kind";
import { TableKindsSectionActions, TableKindsSectionState } from "./table-kinds-section-model";

export type TableKindsSectionDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository">;

export interface TableKindsSectionHost {
  readonly eventId: string;
  /** When the host cancelled the show, as the page has it. */
  readonly cancelledAt?: Rx<Date | null>;
}

/**
 * The tables a saved show sells: kinds the host names, prices and counts, each
 * table numbered across the show. Every answer from the server replaces the
 * kind it describes, so the numbers on screen are the server's.
 *
 * Answers can arrive out of turn, so what is shown follows a few counts. A
 * reading of the tables shows only if it is the last one asked for and no
 * change was answered since it went out. A change is laid on the screen as it
 * is answered, and if a reading was shown or another change answered since its
 * call went out, the tables are read again as well: the server's list is what
 * the screen ends on.
 */
export class TableKindsSectionController extends BaseController implements TableKindsSectionActions {
  readonly state = new TableKindsSectionState();
  #storeId: string | null = null;
  /** Counts the shops shown here, one after another. */
  #visit = 0;
  #asked = 0;
  #shown = 0;
  #changes = 0;
  /** Counts the edit forms opened and closed: an answer is for the form its save came from. */
  #form = 0;
  /** The fields the host changed in the edit form since it opened: a save sends those and no others. */
  #typed = new Set<TableKindField>();
  /** The kind the sentence about a removal is for, and the table the one about a refund is for. */
  #removeTried: string | null = null;
  #refundTried: string | null = null;

  constructor(
    subtree: SubtreeModel,
    readonly deps: TableKindsSectionDeps,
    readonly host: TableKindsSectionHost,
  ) {
    super();
    subtree.put(TableKindsSectionState, this.state);
    subtree.put(TableKindsSectionActions, this);
    this.sync(() => {
      const storeId = deps.storeRepository.shopId;
      if (storeId === this.#storeId) return;
      if (this.#storeId) this.#forget();
      this.#storeId = storeId;
      this.#visit++;
      if (storeId) void this.#load(storeId);
    }, [deps.storeRepository]);
    const cancelledAt = host.cancelledAt;
    if (cancelledAt) {
      this.sync(() => {
        this.state.cancelled.value = cancelledAt.value !== null;
      }, [cancelledAt]);
    }
  }

  /** Reads the tables again, as after the show is cancelled and its payments go back. */
  reload = async () => {
    if (this.#storeId) await this.#load(this.#storeId);
  };

  setField = (field: TableKindField, value: string) => {
    this.state.draft.update((d) => ({ ...d, [field]: value }));
    if (this.state.errors.value[field]) this.state.errors.update(({ [field]: _, ...rest }) => rest);
  };

  add = async () => {
    const storeId = this.#storeId;
    if (!storeId || this.state.addForm.value.busy) return;
    const draft = this.state.draft.value;
    const errors = validateTableKindDraft(draft);
    this.state.errors.value = errors;
    if (hasErrors(errors)) return;
    this.state.addForm.value = busyForm;
    const sent = this.#sent();
    let r: Awaited<ReturnType<typeof createTableKind>>;
    try {
      r = await createTableKind({
        ...this.deps,
        storeId,
        eventId: this.host.eventId,
        body: draftToNewTableKind(draft),
      });
    } catch (err) {
      // It may have been added: read the tables again before another try adds it twice, and let the error reach Sentry.
      if (!this.#gone(sent)) {
        this.state.addForm.value = failedForm(TABLES_FAILED_ON_THE_WAY);
        void this.#load(storeId);
      }
      throw err;
    }
    if (this.#gone(sent)) return;
    if (!r.ok) {
      this.state.addForm.value = failedForm(r.errors);
      return;
    }
    const kind = r.value;
    this.#changed(storeId, sent, () => {
      // A reading shown meanwhile may hold it already, and newer than this answer.
      const listed = this.state.kinds.value.some((k) => k.id === kind.id);
      if (!listed) this.state.kinds.value = [...this.state.kinds.value, kind];
    });
    this.state.draft.value = EMPTY_TABLE_KIND;
    this.state.addForm.value = idleForm;
  };

  startEdit = (kindId: string) => {
    const kind = this.state.kinds.value.find((k) => k.id === kindId);
    if (!kind) return;
    this.#edit(kindId);
    this.state.editDraft.value = tableKindDraft(kind);
    this.state.editErrors.value = {};
  };

  setEditField = (field: TableKindField, value: string) => {
    this.#typed.add(field);
    this.state.editDraft.update((d) => ({ ...d, [field]: value }));
    if (this.state.editErrors.value[field]) this.state.editErrors.update(({ [field]: _, ...rest }) => rest);
  };

  saveEdit = async () => {
    const storeId = this.#storeId;
    const kindId = this.state.editingId.value;
    const kind = this.state.kinds.value.find((k) => k.id === kindId);
    if (!storeId || !kindId || !kind || this.state.editForm.value.busy) return;
    const draft = this.state.editDraft.value;
    // The tables shops hold are a floor for a count being sent, not for one the host left alone.
    const floor = this.#typed.has("quantity") ? takenCount(kind) + payingCount(kind) : 0;
    const errors = validateTableKindDraft(draft, floor);
    this.state.editErrors.value = errors;
    if (hasErrors(errors)) return;
    if (this.#typed.size === 0) {
      this.#edit(null);
      return;
    }
    this.state.editForm.value = busyForm;
    const sent = this.#sent();
    // The host may leave this form before the answer, for another kind's or this kind's again.
    const form = this.#form;
    const stillOpen = () => this.#form === form;
    let r: Awaited<ReturnType<typeof updateTableKind>>;
    try {
      r = await updateTableKind({
        ...this.deps,
        storeId,
        eventId: this.host.eventId,
        kindId,
        body: draftToTableKindUpdate(draft, this.#typed),
      });
    } catch (err) {
      if (!this.#gone(sent)) {
        if (stillOpen()) this.state.editForm.value = failedForm(TABLES_FAILED_ON_THE_WAY);
        void this.#load(storeId);
      }
      throw err;
    }
    if (this.#gone(sent)) return;
    if (!r.ok) {
      if (stillOpen()) this.state.editForm.value = failedForm(r.errors);
      return;
    }
    const saved = r.value;
    // Saved for a form the host has left: a later save of the kind may be on screen, so the server says which too.
    this.#changed(storeId, stillOpen() ? sent : null, () => this.#replace(saved));
    if (stillOpen()) this.#edit(null);
  };

  cancelEdit = () => {
    this.#edit(null);
  };

  remove = async (kindId: string) => {
    const storeId = this.#storeId;
    if (!storeId || this.state.removingId.value) return;
    this.state.removingId.value = kindId;
    this.#removeFailed(null, null);
    const sent = this.#sent();
    let r: Awaited<ReturnType<typeof deleteTableKind>>;
    try {
      r = await deleteTableKind({ ...this.deps, storeId, eventId: this.host.eventId, kindId });
    } catch (err) {
      if (!this.#gone(sent)) {
        this.state.removingId.value = null;
        this.#removeFailed(kindId, TABLES_FAILED_ON_THE_WAY);
        void this.#load(storeId);
      }
      throw err;
    }
    if (this.#gone(sent)) return;
    this.state.removingId.value = null;
    if (!r.ok) {
      // A refusal about a kind the tables no longer list has nothing left to say.
      if (this.state.kinds.value.some((k) => k.id === kindId)) this.#removeFailed(kindId, errorText(r.errors));
      return;
    }
    this.#changed(storeId, sent, () => {
      this.state.kinds.value = this.state.kinds.value.filter((k) => k.id !== kindId);
    });
    if (this.state.editingId.value === kindId) this.#edit(null);
  };

  takeBack = async (tableId: string) => {
    const storeId = this.#storeId;
    if (!storeId || this.state.refundingId.value) return;
    if (this.state.confirmRefundId.value !== tableId) {
      this.state.confirmRefundId.value = tableId;
      this.#refundFailed(null, null);
      return;
    }
    this.state.refundingId.value = tableId;
    const sent = this.#sent();
    let r: Awaited<ReturnType<typeof refundTable>>;
    try {
      r = await refundTable({ ...this.deps, storeId, eventId: this.host.eventId, tableId });
    } catch (err) {
      // The money may have gone back: read the tables again, and let the error reach Sentry.
      if (!this.#gone(sent)) {
        this.state.refundingId.value = null;
        this.state.confirmRefundId.value = null;
        this.#refundFailed(tableId, REFUND_FAILED_ON_THE_WAY);
        void this.#load(storeId);
      }
      throw err;
    }
    if (this.#gone(sent)) return;
    this.state.refundingId.value = null;
    this.state.confirmRefundId.value = null;
    if (!r.ok) {
      // As for a removal: a refusal about a table the tables no longer show as the host's to take back says nothing.
      if (this.#held(tableId, this.state.kinds.value)) this.#refundFailed(tableId, errorText(r.errors));
      return;
    }
    const kind = r.value;
    this.#changed(storeId, sent, () => this.#replace(kind));
  };

  keepTable = () => {
    this.state.confirmRefundId.value = null;
  };

  /** Where things stand as a call goes out: the shop on screen, the readings shown and the changes answered. */
  #sent(): { visit: number; shown: number; changes: number } {
    return { visit: this.#visit, shown: this.#shown, changes: this.#changes };
  }

  /** The section is gone, or has left the shop it showed then: what was asked writes nothing now. */
  #gone(sent: { visit: number }): boolean {
    return this.isDisposed || sent.visit !== this.#visit;
  }

  /**
   * A change the server answered: `show` lays it on the screen, and must do no harm laid on twice. If a
   * reading was shown or another change answered since its call went out (`sent` null says so outright),
   * the screen may be ahead of this answer or behind it, so the tables are read again as well.
   */
  #changed(storeId: string, sent: { shown: number; changes: number } | null, show: () => void): void {
    const crossed = sent?.shown !== this.#shown || sent.changes !== this.#changes;
    this.#changes++;
    show();
    if (crossed) void this.#load(storeId);
    else this.#settle();
  }

  /**
   * The screen holds the newest the server said: the kind a removal failed on is gone, or the table a refund
   * failed on or was asked about is no longer the host's to take back, and nothing is left to say or ask.
   */
  #settle(): void {
    const kinds = this.state.kinds.value;
    if (this.#removeTried && !kinds.some((k) => k.id === this.#removeTried)) this.#removeFailed(null, null);
    if (this.#refundTried && !this.#held(this.#refundTried, kinds)) this.#refundFailed(null, null);
    const asked = this.state.confirmRefundId.value;
    if (asked && !this.#held(asked, kinds)) this.state.confirmRefundId.value = null;
  }

  /** Opens the edit form on a kind, or closes it. */
  #edit(kindId: string | null): void {
    this.#form++;
    this.#typed = new Set();
    this.state.editingId.value = kindId;
    this.state.editForm.value = idleForm;
  }

  /** Another shop is on screen: nothing read, typed or asked for the last one stays. */
  #forget(): void {
    const s = this.state;
    s.status.value = idleLoad;
    s.kinds.value = [];
    s.draft.value = EMPTY_TABLE_KIND;
    s.errors.value = {};
    s.addForm.value = idleForm;
    this.#edit(null);
    s.editErrors.value = {};
    s.removingId.value = null;
    s.confirmRefundId.value = null;
    s.refundingId.value = null;
    this.#refundFailed(null, null);
    this.#removeFailed(null, null);
  }

  #removeFailed(kindId: string | null, sentence: string | null): void {
    this.#removeTried = kindId;
    this.state.removeError.value = sentence;
  }

  #refundFailed(tableId: string | null, sentence: string | null): void {
    this.#refundTried = tableId;
    this.state.refundError.value = sentence;
  }

  /** Whether the tables show a shop holding this one, its payment standing. */
  #held(tableId: string, kinds: readonly EventTableKind[]): boolean {
    return kinds.some((k) => k.tables.some((t) => t.id === tableId && mayTakeBack(t)));
  }

  #replace(kind: EventTableKind): void {
    this.state.kinds.value = this.state.kinds.value.map((k) => (k.id === kind.id ? kind : k));
  }

  async #load(storeId: string): Promise<void> {
    const reading = ++this.#asked;
    const sent = { visit: this.#visit };
    const changes = this.#changes;
    this.state.status.value = loading;
    let r: Awaited<ReturnType<typeof loadTableKinds>>;
    try {
      r = await loadTableKinds({ ...this.deps, storeId, eventId: this.host.eventId });
    } catch (err) {
      if (!this.#gone(sent) && reading === this.#asked) {
        this.state.status.value = { kind: "error", message: TABLES_FAILED_TO_LOAD };
      }
      throw err;
    }
    // A later reading is on its way, and it is the one to show.
    if (this.#gone(sent) || reading !== this.#asked) return;
    // A change was answered after this was asked: it may be older than what is on screen, so read again.
    if (changes !== this.#changes) return this.#load(storeId);
    if (!r.ok) {
      this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.state.kinds.value = r.value.kinds;
    this.#shown++;
    this.#settle();
    this.state.status.value = loaded;
  }
}
